/**
 * Google Tag Manager Data Layer utility
 * Ensures proper initialization and provides helper functions for pushing events
 */

// Note: Window interface is declared in react-app-env.d.ts to avoid duplicate declarations

/**
 * Initialize dataLayer if it doesn't exist
 */
export const initDataLayer = (): void => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
  }
};

/**
 * Check if GTM is loaded and ready
 */
export const isGTMReady = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check if GTM container is loaded
  const hasGTMContainer = window.google_tag_manager && 
                          window.google_tag_manager['GTM-WL9PPHQL'];
  
  // Check if gtm.js event was fired
  const hasGTMJS = window.dataLayer && 
                   window.dataLayer.some((item: any) => item.event === 'gtm.js');
  
  // Check if gtm.load event was fired (GTM is fully loaded)
  const hasGTMLoad = window.dataLayer && 
                     window.dataLayer.some((item: any) => item.event === 'gtm.load');
  
  return !!(hasGTMContainer || hasGTMJS || hasGTMLoad);
};

/**
 * Wait for GTM to be ready before pushing events
 * @param callback - Callback to execute after GTM is ready
 * @param timeout - Maximum time to wait in milliseconds (default: 3000ms)
 */
export const waitForGTM = (callback: () => void, timeout: number = 3000): void => {
  if (typeof window === 'undefined') {
    callback();
    return;
  }

  initDataLayer();

  // If GTM is already ready, execute immediately
  if (isGTMReady()) {
    callback();
    return;
  }

  const startTime = Date.now();
  
  const checkGTM = () => {
    if (isGTMReady() || Date.now() - startTime > timeout) {
      callback();
    } else {
      setTimeout(checkGTM, 50); // Check more frequently
    }
  };

  checkGTM();
};

/**
 * Push event to Data Layer with proper error handling
 * @param eventData - Event data to push to Data Layer
 */
export const pushToDataLayer = (eventData: Record<string, any>): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize dataLayer if it doesn't exist
  initDataLayer();

  try {
    // Ensure event property exists (required for GTM triggers)
    if (!eventData.event) {
      console.warn('Data Layer event missing "event" property:', eventData);
    }

    // Push event to Data Layer
    window.dataLayer.push(eventData);
    
    // Always log for debugging (can be removed in production if needed)
    console.log('[Data Layer] Event Pushed:', {
      event: eventData.event,
      timestamp: new Date().toISOString(),
      data: eventData,
      dataLayerLength: window.dataLayer.length
    });
  } catch (error) {
    console.error('[Data Layer] Error pushing event:', error, eventData);
  }
};

/**
 * Push event to Data Layer (always waits for GTM to be ready)
 * @param eventData - Event data to push
 * @param immediate - If true, push immediately without waiting (default: false)
 */
export const pushEvent = (
  eventData: Record<string, any>,
  immediate: boolean = false
): void => {
  if (immediate) {
    // Push immediately (GTM can catch events pushed before it loads)
    pushToDataLayer(eventData);
  } else {
    // Wait for GTM to be ready, then push
    waitForGTM(() => {
      pushToDataLayer(eventData);
    });
  }
};

/**
 * Debug helper: Log current Data Layer state
 */
export const debugDataLayer = (): void => {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.log('[Data Layer Debug] Data Layer not initialized');
    return;
  }

  console.group('[Data Layer Debug]');
  console.log('Data Layer Length:', window.dataLayer.length);
  console.log('GTM Ready:', isGTMReady());
  console.log('GTM Container:', window.google_tag_manager?.['GTM-WL9PPHQL'] ? 'Loaded' : 'Not Loaded');
  console.log('All Events:', window.dataLayer);
  console.log('Recent Events (last 10):', window.dataLayer.slice(-10));
  console.groupEnd();
};

// Expose debug function to window for easy access in console
if (typeof window !== 'undefined') {
  (window as any).debugDataLayer = debugDataLayer;
}

